export type KeypadTab = "123" | "ops" | "greek" | "struct" | "func" | "abc" | "chem" | "phys" | "stat" | "eng";

export interface KeyDef {
  label: string;
  latex: string;
  display?: string;
  wide?: boolean;
}

export const KEYPAD_TABS: { key: KeypadTab; label: string; icon?: string }[] = [
  { key: "123", label: "123", icon: "🔢" },
  { key: "ops", label: "±≤", icon: "≠" },
  { key: "abc", label: "abc", icon: "x" },
  { key: "greek", label: "αβγ", icon: "α" },
  { key: "struct", label: "∑∫", icon: "∑" },
  { key: "func", label: "f(x)", icon: "f" },
  { key: "phys", label: "Physics", icon: "⟨ψ⟩" },
  { key: "chem", label: "Chemistry", icon: "⚗" },
  { key: "stat", label: "Statistics", icon: "σ" },
  { key: "eng", label: "Engineering", icon: "⚡" },
];

// Special key values
export const KEY_BACKSPACE = "__BACKSPACE__";
export const KEY_NEWLINE = "__NEWLINE__";

export function handleKeyPress(
  key: KeyDef,
  currentLatex: string,
  onChange: (latex: string) => void
): string {
  if (key.latex === KEY_BACKSPACE) {
    const trimmed = currentLatex.trimEnd();
    const cmdMatch = trimmed.match(/\\[a-zA-Z]+\s?$/);
    const next = cmdMatch ? trimmed.slice(0, -cmdMatch[0].length) : trimmed.slice(0, -1);
    onChange(next);
    return next;
  }
  if (key.latex === KEY_NEWLINE) {
    const next = currentLatex + " \\\\\n";
    onChange(next);
    return next;
  }
  const next = currentLatex + key.latex;
  onChange(next);
  return next;
}

export const KEYS: Record<KeypadTab, KeyDef[]> = {
  "123": [
    { label: "7", latex: "7" },
    { label: "8", latex: "8" },
    { label: "9", latex: "9" },
    { label: "÷", latex: "\\div " },
    { label: "⌫", latex: KEY_BACKSPACE },
    { label: "4", latex: "4" },
    { label: "5", latex: "5" },
    { label: "6", latex: "6" },
    { label: "×", latex: "\\times " },
    { label: "(", latex: "(" },
    { label: "1", latex: "1" },
    { label: "2", latex: "2" },
    { label: "3", latex: "3" },
    { label: "−", latex: "-" },
    { label: ")", latex: ")" },
    { label: "0", latex: "0" },
    { label: ".", latex: "." },
    { label: "=", latex: "=" },
    { label: "+", latex: "+" },
    { label: "⏎", latex: KEY_NEWLINE },
  ],
  ops: [
    { label: "≤", latex: "\\leq " }, { label: "≥", latex: "\\geq " },
    { label: "≠", latex: "\\neq " }, { label: "≈", latex: "\\approx " },
    { label: "±", latex: "\\pm " }, { label: "∈", latex: "\\in " },
    { label: "∉", latex: "\\notin " }, { label: "⊂", latex: "\\subset " },
    { label: "⊆", latex: "\\subseteq " }, { label: "∞", latex: "\\infty " },
    { label: "→", latex: "\\rightarrow " }, { label: "⇒", latex: "\\Rightarrow " },
    { label: "⇔", latex: "\\Leftrightarrow " }, { label: "↦", latex: "\\mapsto " },
    { label: "∂", latex: "\\partial " }, { label: "∀", latex: "\\forall " },
    { label: "∃", latex: "\\exists " }, { label: "∧", latex: "\\land " },
    { label: "∨", latex: "\\lor " }, { label: "¬", latex: "\\neg " },
    { label: "∪", latex: "\\cup " }, { label: "∩", latex: "\\cap " },
    { label: "∅", latex: "\\emptyset " }, { label: "·", latex: "\\cdot " },
    { label: "∝", latex: "\\propto " }, { label: "≡", latex: "\\equiv " },
    { label: "∼", latex: "\\sim " }, { label: "≪", latex: "\\ll " },
    { label: "≫", latex: "\\gg " }, { label: "†", latex: "\\dagger " },
  ],
  abc: [
    { label: "x", latex: "x" }, { label: "y", latex: "y" },
    { label: "z", latex: "z" }, { label: "a", latex: "a" },
    { label: "b", latex: "b" }, { label: "c", latex: "c" },
    { label: "n", latex: "n" }, { label: "m", latex: "m" },
    { label: "i", latex: "i" }, { label: "j", latex: "j" },
    { label: "k", latex: "k" }, { label: "t", latex: "t" },
    { label: "f", latex: "f" }, { label: "g", latex: "g" },
    { label: "h", latex: "h" }, { label: "ℝ", latex: "\\mathbb{R}" },
    { label: "ℤ", latex: "\\mathbb{Z}" }, { label: "ℕ", latex: "\\mathbb{N}" },
    { label: "ℂ", latex: "\\mathbb{C}" }, { label: "ℚ", latex: "\\mathbb{Q}" },
    { label: "x̂", latex: "\\hat{}" }, { label: "x̄", latex: "\\bar{}" },
    { label: "x⃗", latex: "\\vec{}" }, { label: "ẋ", latex: "\\dot{}" },
    { label: "x̃", latex: "\\tilde{}" }, { label: "𝐱", latex: "\\mathbf{}" },
    { label: "𝒳", latex: "\\mathcal{}" }, { label: "x̲", latex: "\\underline{}" },
    { label: "x̅", latex: "\\overline{}" }, { label: "sp", latex: "\\, " },
  ],
  greek: [
    { label: "α", latex: "\\alpha " }, { label: "β", latex: "\\beta " },
    { label: "γ", latex: "\\gamma " }, { label: "δ", latex: "\\delta " },
    { label: "ε", latex: "\\epsilon " }, { label: "ζ", latex: "\\zeta " },
    { label: "η", latex: "\\eta " }, { label: "θ", latex: "\\theta " },
    { label: "λ", latex: "\\lambda " }, { label: "μ", latex: "\\mu " },
    { label: "ν", latex: "\\nu " }, { label: "ξ", latex: "\\xi " },
    { label: "π", latex: "\\pi " }, { label: "ρ", latex: "\\rho " },
    { label: "σ", latex: "\\sigma " }, { label: "τ", latex: "\\tau " },
    { label: "φ", latex: "\\phi " }, { label: "χ", latex: "\\chi " },
    { label: "ψ", latex: "\\psi " }, { label: "ω", latex: "\\omega " },
    { label: "Γ", latex: "\\Gamma " }, { label: "Δ", latex: "\\Delta " },
    { label: "Θ", latex: "\\Theta " }, { label: "Λ", latex: "\\Lambda " },
    { label: "Σ", latex: "\\Sigma " }, { label: "Φ", latex: "\\Phi " },
    { label: "Ψ", latex: "\\Psi " }, { label: "Ω", latex: "\\Omega " },
    { label: "Π", latex: "\\Pi " }, { label: "ϕ", latex: "\\varphi " },
  ],
  struct: [
    { label: "a/b", latex: "\\frac{}{}" }, { label: "√", latex: "\\sqrt{}" },
    { label: "ⁿ√", latex: "\\sqrt[]{}" }, { label: "xⁿ", latex: "^{}" },
    { label: "xₙ", latex: "_{}" }, { label: "∑", latex: "\\sum_{}^{}" },
    { label: "∏", latex: "\\prod_{}^{}" }, { label: "∫", latex: "\\int_{}^{}" },
    { label: "∬", latex: "\\iint " }, { label: "∮", latex: "\\oint " },
    { label: "lim", latex: "\\lim_{\\to }" }, { label: "(n k)", latex: "\\binom{}{}" },
    { label: "( )", latex: "\\left(  \\right)" }, { label: "[ ]", latex: "\\left[  \\right]" },
    { label: "{ }", latex: "\\left\\{  \\right\\}" }, { label: "| |", latex: "\\left|  \\right|" },
    { label: "‖ ‖", latex: "\\left\\|  \\right\\|" }, { label: "⟨ ⟩", latex: "\\langle  \\rangle" },
    { label: "M 2×2", latex: "\\begin{pmatrix}  &  \\\\  &  \\end{pmatrix}" },
    { label: "cases", latex: "\\begin{cases}  \\\\  \\end{cases}" },
    { label: "align", latex: " &=  \\\\\n" }, { label: "text", latex: "\\text{}" },
    { label: "⌊ ⌋", latex: "\\lfloor  \\rfloor" }, { label: "⌈ ⌉", latex: "\\lceil  \\rceil" },
    { label: "…", latex: "\\cdots " }, { label: "⋮", latex: "\\vdots " },
    { label: "⋱", latex: "\\ddots " }, { label: "°", latex: "^{\\circ}" },
    { label: "d/dx", latex: "\\frac{d}{dx}" }, { label: "∂/∂x", latex: "\\frac{\\partial}{\\partial x}" },
  ],
  func: [
    { label: "sin", latex: "\\sin " }, { label: "cos", latex: "\\cos " },
    { label: "tan", latex: "\\tan " }, { label: "sec", latex: "\\sec " },
    { label: "csc", latex: "\\csc " }, { label: "cot", latex: "\\cot " },
    { label: "sin⁻¹", latex: "\\sin^{-1}" }, { label: "cos⁻¹", latex: "\\cos^{-1}" },
    { label: "tan⁻¹", latex: "\\tan^{-1}" }, { label: "ln", latex: "\\ln " },
    { label: "log", latex: "\\log " }, { label: "log₂", latex: "\\log_2 " },
    { label: "exp", latex: "\\exp " }, { label: "eˣ", latex: "e^{}" },
    { label: "det", latex: "\\det " }, { label: "min", latex: "\\min " },
    { label: "max", latex: "\\max " }, { label: "sup", latex: "\\sup " },
    { label: "inf", latex: "\\inf " }, { label: "arg", latex: "\\arg " },
    { label: "dim", latex: "\\dim " }, { label: "ker", latex: "\\ker " },
    { label: "gcd", latex: "\\gcd " }, { label: "mod", latex: "\\mod " },
    { label: "∇", latex: "\\nabla " }, { label: "∇·", latex: "\\nabla \\cdot " },
    { label: "∇×", latex: "\\nabla \\times " }, { label: "∇²", latex: "\\nabla^2 " },
    { label: "ℏ", latex: "\\hbar " }, { label: "ℓ", latex: "\\ell " },
  ],
  phys: [
    { label: "⟨ψ|", latex: "\\langle  |" }, { label: "|ψ⟩", latex: "|  \\rangle" },
    { label: "⟨ψ|φ⟩", latex: "\\langle  |  \\rangle" },
    { label: "⟨A⟩", latex: "\\langle  \\rangle" },
    { label: "⟨ψ|A|φ⟩", latex: "\\langle  |  |  \\rangle" },
    { label: "[A,B]", latex: "\\left[  ,  \\right]" },
    { label: "{A,B}", latex: "\\left\\{  ,  \\right\\}" },
    { label: "d/dx", latex: "\\frac{d}{dx}" },
    { label: "∂/∂x", latex: "\\frac{\\partial}{\\partial x}" },
    { label: "∂²/∂x²", latex: "\\frac{\\partial^2}{\\partial x^2}" },
    { label: "dx", latex: "\\,dx" }, { label: "δ/δf", latex: "\\frac{\\delta}{\\delta f}" },
    { label: "∇", latex: "\\nabla " }, { label: "∇·", latex: "\\nabla \\cdot " },
    { label: "∇×", latex: "\\nabla \\times " }, { label: "∇²", latex: "\\nabla^2 " },
    { label: "v⃗", latex: "\\vec{}" }, { label: "v̂", latex: "\\hat{}" },
    { label: "𝐯", latex: "\\mathbf{}" }, { label: "ℏ", latex: "\\hbar " },
    { label: "kB", latex: "k_B " }, { label: "ε₀", latex: "\\varepsilon_0 " },
    { label: "μ₀", latex: "\\mu_0 " }, { label: "c", latex: "c " },
    { label: "G", latex: "G " }, { label: "eV", latex: "\\,\\text{eV}" },
    { label: "Â", latex: "\\hat{}" }, { label: "†", latex: "^{\\dagger}" },
    { label: "⊗", latex: "\\otimes " }, { label: "⊕", latex: "\\oplus " },
  ],
  chem: [
    { label: "ce{}", latex: "\\ce{}" }, { label: "→", latex: "\\ce{->}" },
    { label: "⇌", latex: "\\ce{<=>}" }, { label: "⟶", latex: "\\ce{->[above][below]}" },
    { label: "↑", latex: "\\ce{^}" }, { label: "↓", latex: "\\ce{v}" },
    { label: "H₂O", latex: "\\ce{H2O}" }, { label: "CO₂", latex: "\\ce{CO2}" },
    { label: "NaCl", latex: "\\ce{NaCl}" }, { label: "H₂SO₄", latex: "\\ce{H2SO4}" },
    { label: "NaOH", latex: "\\ce{NaOH}" }, { label: "NH₃", latex: "\\ce{NH3}" },
    { label: "(aq)", latex: "\\ce{(aq)}" }, { label: "(s)", latex: "\\ce{(s)}" },
    { label: "(l)", latex: "\\ce{(l)}" }, { label: "(g)", latex: "\\ce{(g)}" },
    { label: "−", latex: "\\ce{-}" }, { label: "=", latex: "\\ce{=}" },
    { label: "≡", latex: "\\ce{#}" }, { label: "¹⁴C", latex: "\\ce{^{14}_{6}C}" },
    { label: "²H", latex: "\\ce{^{2}H}" }, { label: "+", latex: "\\ce{+}" },
    { label: "²⁺", latex: "^{2+}" }, { label: "³⁻", latex: "^{3-}" },
    { label: "ΔH", latex: "\\Delta H" }, { label: "ΔG", latex: "\\Delta G" },
    { label: "ΔS", latex: "\\Delta S" }, { label: "K_eq", latex: "K_{\\text{eq}}" },
    { label: "K_a", latex: "K_a " }, { label: "K_sp", latex: "K_{\\text{sp}}" },
    { label: "[A]", latex: "[\\text{}]" }, { label: "mol/L", latex: "\\,\\text{mol/L}" },
    { label: "pH", latex: "\\text{pH}" }, { label: "pKa", latex: "\\text{p}K_a" },
  ],
  stat: [
    { label: "P(A)", latex: "P()" }, { label: "P(A|B)", latex: "P( \\mid )" },
    { label: "P(A∩B)", latex: "P( \\cap )" }, { label: "P(A∪B)", latex: "P( \\cup )" },
    { label: "E[X]", latex: "\\mathbb{E}\\left[  \\right]" },
    { label: "Var(X)", latex: "\\text{Var}()" },
    { label: "Cov", latex: "\\text{Cov}(,)" }, { label: "Corr", latex: "\\text{Corr}(,)" },
    { label: "σ²", latex: "\\sigma^2" }, { label: "σ", latex: "\\sigma " },
    { label: "μ", latex: "\\mu " }, { label: "x̄", latex: "\\bar{x}" },
    { label: "s²", latex: "s^2" },
    { label: "N(μ,σ²)", latex: "\\mathcal{N}(\\mu, \\sigma^2)" },
    { label: "Bin(n,p)", latex: "\\text{Bin}(n, p)" },
    { label: "Pois(λ)", latex: "\\text{Pois}(\\lambda)" },
    { label: "Exp(λ)", latex: "\\text{Exp}(\\lambda)" },
    { label: "U(a,b)", latex: "\\text{U}(a, b)" },
    { label: "χ²", latex: "\\chi^2" }, { label: "t(ν)", latex: "t_{\\nu}" },
    { label: "F(d₁,d₂)", latex: "F_{d_1, d_2}" },
    { label: "H₀", latex: "H_0" }, { label: "H₁", latex: "H_1" },
    { label: "p-val", latex: "p\\text{-value}" },
    { label: "α", latex: "\\alpha " }, { label: "β", latex: "\\beta " },
    { label: "(n k)", latex: "\\binom{n}{k}" }, { label: "n!", latex: "n!" },
    { label: "nPr", latex: "P(n,r)" }, { label: "nCr", latex: "C(n,r)" },
    { label: "Σxᵢ", latex: "\\sum_{i=1}^{n} x_i" },
    { label: "Πxᵢ", latex: "\\prod_{i=1}^{n} x_i" },
    { label: "1/n Σ", latex: "\\frac{1}{n}\\sum_{i=1}^{n}" },
    { label: "ŷ", latex: "\\hat{y}" }, { label: "R²", latex: "R^2" },
    { label: "β₀", latex: "\\beta_0 " }, { label: "β₁", latex: "\\beta_1 " },
  ],
  eng: [
    { label: "ℒ{}", latex: "\\mathcal{L}\\{\\}" },
    { label: "ℒ⁻¹{}", latex: "\\mathcal{L}^{-1}\\{\\}" },
    { label: "ℱ{}", latex: "\\mathcal{F}\\{\\}" },
    { label: "ℱ⁻¹{}", latex: "\\mathcal{F}^{-1}\\{\\}" },
    { label: "𝒵{}", latex: "\\mathcal{Z}\\{\\}" },
    { label: "H(s)", latex: "H(s)" }, { label: "H(jω)", latex: "H(j\\omega)" },
    { label: "G(s)", latex: "G(s)" }, { label: "Y/X", latex: "\\frac{Y(s)}{X(s)}" },
    { label: "jω", latex: "j\\omega " }, { label: "e^jωt", latex: "e^{j\\omega t}" },
    { label: "e^st", latex: "e^{st}" }, { label: "δ(t)", latex: "\\delta(t)" },
    { label: "u(t)", latex: "u(t)" }, { label: "Re{}", latex: "\\text{Re}\\{\\}" },
    { label: "Im{}", latex: "\\text{Im}\\{\\}" },
    { label: "|H|", latex: "|H(j\\omega)|" }, { label: "∠H", latex: "\\angle H(j\\omega)" },
    { label: "dB", latex: "\\,\\text{dB}" }, { label: "Hz", latex: "\\,\\text{Hz}" },
    { label: "rad/s", latex: "\\,\\text{rad/s}" }, { label: "V", latex: "\\,\\text{V}" },
    { label: "A", latex: "\\,\\text{A}" }, { label: "Ω", latex: "\\,\\Omega" },
    { label: "W", latex: "\\,\\text{W}" }, { label: "F", latex: "\\,\\text{F}" },
    { label: "H", latex: "\\,\\text{H}" },
    { label: "Ax=b", latex: "\\mathbf{A}\\mathbf{x}=\\mathbf{b}" },
    { label: "det A", latex: "\\det(\\mathbf{A})" },
    { label: "A⁻¹", latex: "\\mathbf{A}^{-1}" }, { label: "Aᵀ", latex: "\\mathbf{A}^T" },
    { label: "λ", latex: "\\lambda " },
    { label: "ẋ=Ax", latex: "\\dot{\\mathbf{x}} = \\mathbf{A}\\mathbf{x}" },
    { label: "∞-norm", latex: "\\|\\|_{\\infty}" },
  ],
};
